import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import OpenAI from "openai";
import { motion, AnimatePresence } from 'framer-motion';
import chineseImg from '../images/chinese.png';
import spinningGif from '../images/spinning.gif';
import PromptEditor from './PromptEditor';
import MarkdownRenderer from './MarkdownRenderer';
import ShareButtons from "./common/ShareButtons";
import { getWalletData, fetchWalletSummary, formatTradeSummary } from '../services/walletService';
import CompassAnimation from './CompassAnimation';

// Import necessary image resources
import bgMain from "../images/bg-main.png";

const HoroscopeDetail = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('compass'); // 'compass', 'form' or 'result'
  const [personalData, setPersonalData] = useState({
    birthDate: '',
    birthTime: '00:00-01:00', // 设置默认值为第一个选项
    birthPlace: '',
    gender: '',
    language: 'english' // 默认使用中文输出
  });
  
  // 生成出生时间选项
  const birthTimeOptions = [
    { value: '00:00-01:00', label: '00:00–01:00' },
    { value: '01:00-02:00', label: '01:00–02:00' },
    { value: '02:00-03:00', label: '02:00–03:00' },
    { value: '03:00-04:00', label: '03:00–04:00' },
    { value: '04:00-05:00', label: '04:00–05:00' },
    { value: '05:00-06:00', label: '05:00–06:00' },
    { value: '06:00-07:00', label: '06:00–07:00' },
    { value: '07:00-08:00', label: '07:00–08:00' },
    { value: '08:00-09:00', label: '08:00–09:00' },
    { value: '09:00-10:00', label: '09:00–10:00' },
    { value: '10:00-11:00', label: '10:00–11:00' },
    { value: '11:00-12:00', label: '11:00–12:00' },
    { value: '12:00-13:00', label: '12:00–13:00' },
    { value: '13:00-14:00', label: '13:00–14:00' },
    { value: '14:00-15:00', label: '14:00–15:00' },
    { value: '15:00-16:00', label: '15:00–16:00' },
    { value: '16:00-17:00', label: '16:00–17:00' },
    { value: '17:00-18:00', label: '17:00–18:00' },
    { value: '18:00-19:00', label: '18:00–19:00' },
    { value: '19:00-20:00', label: '19:00–20:00' },
    { value: '20:00-21:00', label: '20:00–21:00' },
    { value: '21:00-22:00', label: '21:00–22:00' },
    { value: '22:00-23:00', label: '22:00–23:00' },
    { value: '23:00-00:00', label: '23:00–00:00' },
    { value: 'unknown', label: 'I don\'t know' }
  ];
  const [result, setResult] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamComplete, setStreamComplete] = useState(false);
  const [aiStatusText, setAiStatusText] = useState('AI is thinking');

  // 轮换AI状态文本
  useEffect(() => {
    if (isLoading && !streamComplete) {
      const statusTexts = [
        'AI is generating answers...',
        'AI is thinking...',
        'AI is perceiving...',
        'AI is calculating...'
      ];
      let currentIndex = 0;
      
      const intervalId = setInterval(() => {
        currentIndex = (currentIndex + 1) % statusTexts.length;
        setAiStatusText(statusTexts[currentIndex]);
      }, 1000);
      
      return () => clearInterval(intervalId);
    }
  }, [isLoading, streamComplete]);
  // 钱包数据相关状态
  const [walletAddress, setWalletAddress] = useState('');
  const [walletData, setWalletData] = useState(null);
  const [isFetchingWallet, setIsFetchingWallet] = useState(false);
  const [walletSummary, setWalletSummary] = useState('');
  const [currentPrompt, setCurrentPrompt] = useState('');

  const resultRef = useRef(null);

  // 获取钱包数据
  const fetchWalletData = useCallback(async () => {
    try {
      setIsFetchingWallet(true);
      const savedAddress = localStorage.getItem('bscAddress');
      
      if (!savedAddress) {
        console.log('没有找到钱包地址');
        return;
      }
      
      setWalletAddress(savedAddress);
      
      // 检查是否有缓存数据
      const cachedData = getWalletData();
      if (cachedData && cachedData.address === savedAddress) {
        console.log('使用缓存的钱包数据');
        setWalletData(cachedData);
        const formattedSummary = formatTradeSummary(cachedData);
        setWalletSummary(formattedSummary);
        return;
      }
      
      // 没有缓存数据，获取新数据
      console.log('获取新的钱包数据');
      const data = await fetchWalletSummary(savedAddress);
      setWalletData(data);
      const formattedSummary = formatTradeSummary(data);
      setWalletSummary(formattedSummary);
    } catch (error) {
      console.error('获取钱包数据失败:', error);
    } finally {
      setIsFetchingWallet(false);
    }
  }, []);

  // 在组件加载时获取钱包数据
  useEffect(() => {
    fetchWalletData();
  }, [fetchWalletData]);

  // 定义 updatePrompt，统一生成 prompt
  const updatePrompt = useCallback(() => {
    // 获取当前时间并格式化
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const day = now.getDate();
    const formattedDate = `${year}年${month}月${day}日`;
    const dataString = `**出生时间**：农历${personalData.birthDate} ${personalData.birthTime}点
**出生地**：${personalData.birthPlace}
**性别**：${personalData.gender === 'Male' ? '男' : '女'}`;

    // 根据语言选择调整提示词
    let languageInstruction = '';
    if (personalData.language === 'english') {
      languageInstruction = '请用英文输出所有内容。';
    }

    const updatedPrompt = `你是一位精通加密市场分析与命理周期推演的专家，请基于以下用户信息和链上交易数据生成一份《加密玩家财富周期指南》报告。报告需结合用户的命理特质与实际交易数据，提供个性化的财富周期分析、持仓建议、历史财运回溯、未来十年财运预测、未来六个月关键交易窗口、交易策略优化以及风险管理措施。
请以 Markdown 格式（千万不要用markdown 中的marmaid 和 diff格式， 也不要再在开头带上markdown的标识！）输出完整报告，并确保内容结构清晰、数据详实、分析有据。
因为是面向用户的，要有趣，然后不要有什么报告总结。
${languageInstruction}

### 用户信息
${dataString}

### 用户的真实的 web3 币安的持仓和 swap 数据
${walletSummary}

### 生成要求 

1. **核心能力画像**
   - 综合命理分析与交易数据，评估用户的交易风格，并用评分表呈现以下五个维度：
all the 5 numbers are filled a number between 0-100， 说明也应该按照数的大小变化，而且说明里面最好说出来哪些币在钱包里面， 交易的怎么样，不要说的很空洞:
     - 短线交易能力（例如：{a score in 0-100 for if trade frequent, then score is high}/100，说明：{}）
     - 持仓稳定性（例如：{a score in 0-100 for is there any token that user holds longer}/100，说明：{}）
     - 市场洞察力（例如：{a score in 0-100 for is the user's trading pnl positive and high or negative and low}/100，说明：{}）
     - 风险管理（例如：{a score in 0-100 for the max drawdown of the strategy}/100，说明：{}）
     - 资金配置合理性（例如：{a score in 0-100 for how diverse is the holding}/100，说明：{}）

具体的一个例子是：

    - 短线交易能力（例如：70/100，说明：命理适合中高频操作，但实际交易频率偏低）
     - 持仓稳定性（例如：65/100，说明：部分资产持有较长，但整体波动较大）
     - 市场洞察力（例如：75/100，说明：具备较强的市场预判能力）
     - 风险管理（例如：50/100，说明：止损策略执行不足，风险较高）
     - 资金配置合理性（例如：60/100，说明：资产分散但存在优化空间）


2. **持仓架构分析**\n   - 根据市值将持仓分为核心资产、机会型资产、对冲策略资产，并提供优化建议.

3. **过去 3 年财运回溯**\n   - 回顾过去三年的收益与亏损，分析优势和不足，提出改进建议.

4. **未来 5 年财运预测（2025-2030）**\n   - 预测每年财富趋势，给出年度投资建议，使用表格形式展示.

5. **作战日历（未来 6 个月关键时间窗口，当前时间是：${formattedDate}）**\n   - 以对用户展示友好的形式展示未来六个月的关键交易窗口，包括资金管理和风险控制.
`;
    setCurrentPrompt(updatedPrompt);
  }, [personalData, walletSummary]);

  useEffect(() => {
    updatePrompt();
  }, [updatePrompt]);

  // 检查所有必填字段是否已填写
  const isFormValid = () => {
    return (
      personalData.birthDate.trim() !== '' && 
      personalData.birthTime !== '' && 
      personalData.birthPlace.trim() !== '' && 
      personalData.gender !== ''
    );
  };

  // Handle form input
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPersonalData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Return to home page
  const handleGoBack = () => {
    navigate('/');
  };

  // 用于存储当前API请求的控制器
  const [controller, setController] = useState(null);

  // Return to form page or interrupt API output
  const handleBackToForm = () => {
    if (!streamComplete && step === 'result') {
      // If stream is not complete, interrupt it and go back to form
      if (controller) {
        controller.abort(); // 中断API请求
      }
      setIsLoading(false);
      setStep('form');
      setResult('');
      setStreamComplete(false);
    } else {
      // If stream is complete, just go back to form
      setStep('form');
      setResult('');
      setStreamComplete(false);
    }
    
    // 确保重置加载状态，解决按钮无法再次点击的问题
    setTimeout(() => {
      setIsLoading(false);
    }, 100);
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Build request data
      const dataString = `**出生时间**：农历${personalData.birthDate} ${personalData.birthTime}
**出生地**：${personalData.birthPlace}
**性别**：${personalData.gender === 'Male' ? '男' : personalData.gender === 'Female' ? '女' : '不愿透露'}`;

      // Set step to result and start request
      setStep('result');
      
      // Create OpenAI client
      const openai = new OpenAI({
        baseURL: 'https://api.deepseek.com',
        apiKey: 'sk-326e542c536742f3bb5f4d8b019a614e',
        dangerouslyAllowBrowser: true
      });

      
      // 创建一个可中止的控制器
      const abortController = new AbortController();
      setController(abortController);
      
      try {
        // Stream results with abort signal
        const stream = await openai.chat.completions.create({
          messages: [{ role: "user", content: currentPrompt }],
          model: "deepseek-chat",
          stream: true,
        }, { signal: abortController.signal });

        let fullResponse = '';
        for await (const chunk of stream) {
          const content = chunk.choices[0]?.delta?.content || '';
          if (content) {
            fullResponse += content;
            setResult(fullResponse);
            
            // Ensure scrolling to bottom to show latest content
            if (resultRef.current) {
              // 使用setTimeout确保DOM更新后再滚动
              setTimeout(() => {
                if (resultRef.current) {
                  resultRef.current.scrollTop = resultRef.current.scrollHeight;
                }
              }, 0);
            }
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('API request was aborted');
        } else {
          throw error;
        }
      }
      
      setStreamComplete(true);
      
    } catch (error) {
      console.error('Request error:', error);
      setResult('Sorry, an error occurred during analysis. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendPrompt = async (prompt) => {
    if (isLoading) return;
    setIsLoading(true);
    setStep('result');
    try {
      const abortController = new AbortController();
      setController(abortController);
      const openai = new OpenAI({
        baseURL: 'https://api.deepseek.com',
        apiKey: 'sk-326e542c536742f3bb5f4d8b019a614e',
        dangerouslyAllowBrowser: true
      });
      try {
        const stream = await openai.chat.completions.create({
          messages: [{ role: "system", content: prompt }],
          model: "deepseek-chat",
          stream: true,
        }, { signal: abortController.signal });
        let fullResponse = '';
        for await (const chunk of stream) {
          const content = chunk.choices[0]?.delta?.content || '';
          if (content) {
            fullResponse += content;
            setResult(fullResponse);
            if (resultRef.current) {
              setTimeout(() => {
                if (resultRef.current) {
                  resultRef.current.scrollTop = resultRef.current.scrollHeight;
                }
              }, 0);
            }
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('API request was aborted');
        } else {
          throw error;
        }
      }
      setStreamComplete(true);
    } catch (error) {
      console.error('Request error:', error);
      setResult('Sorry, an error occurred during analysis. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // 不再需要自定义的 renderMarkdown 函数，使用 MarkdownRenderer 组件代替

  // 确保每次结果更新时滚动到底部
  useEffect(() => {
    if (resultRef.current && result) {
      if (resultRef.current) {
        resultRef.current.scrollTop = resultRef.current.scrollHeight;
      }
    }
  }, [result, streamComplete]);

  // Star background animation
  // 组件卸载时清理资源
  useEffect(() => {
    return () => {
      if (controller) {
        controller.abort();
      }
    };
  }, [controller]);

  const StarBackground = () => {
    return (
      <div className="absolute inset-0 overflow-hidden z-0">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-white"
            initial={{
              width: Math.random() * 3 + 1,
              height: Math.random() * 3 + 1,
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              opacity: Math.random() * 0.5 + 0.3
            }}
            animate={{
              opacity: [0.3, 0.8, 0.3],
              scale: [1, 1.2, 1]
            }}
            transition={{
              duration: Math.random() * 4 + 3,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="w-full min-h-screen bg-element-lightwhite overflow-hidden relative">
      {/* Background image */}
      <img
        className="w-full h-full object-cover absolute top-0 left-0 z-0 opacity-40"
        alt="Background"
        src={bgMain}
      />

      {/* Star background  */}
      <StarBackground />

      {/* Content area */}
      <div className="relative z-10 px-4 min-h-screen flex flex-col w-full max-w-full h-screen overflow-hidden">
        {/* 绝对定位的返回按钮 - 在罗盘页面不显示 */}
        {step !== "compass" && (
          <motion.button
            onClick={step === "form" ? handleGoBack : handleBackToForm}
            className="absolute top-6 left-4 flex items-center justify-center bg-white rounded-full p-2 shadow-md w-10 h-10 z-30"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </motion.button>
        )}

        {/* Title - 只在非罗盘页面显示 */}
        {step !== "compass" && (
          <motion.div
            className="text-center mb-4 mt-16"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h1 className="text-2xl font-bold text-[#1c1f25]">
              {step === "form"
                ? "Fortune Telling by AI"
                : "Fortune Telling by AI "}
            </h1>
            <p className="text-[#676C77] mt-2 flex items-center justify-center">
              {step === "form" ? (
                "Let AI reveal your financial destiny through ancient birth-based wisdom — personalized just for you and powered by DeepSeek"
              ) : (
                <>
                  Your personalized Fortune Telling
                  {personalData.language === "chinese" && (
                    <img
                      src={chineseImg}
                      alt="Chinese Fortune"
                      className="ml-2 h-5 inline-block"
                    />
                  )}
                </>
              )}
            </p>
          </motion.div>
        )}

        <AnimatePresence mode="wait">
          {/* Compass Animation */}
          {step === "compass" && (
            <motion.div
              className="flex-1 flex items-center justify-center h-[calc(100vh-100px)]"
              key="compass"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <CompassAnimation onComplete={() => setStep("form")} />
            </motion.div>
          )}

          {/* Form page */}
          {step === "form" && (
            <motion.form
              onSubmit={handleSubmit}
              className="w-full bg-white/90 backdrop-blur-sm rounded-[16px] p-6 shadow-lg h-[calc(100vh-160px)] overflow-y-auto flex flex-col"
              key="form"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Birth Date
                  </label>
                  <motion.input
                    whileFocus={{ scale: 1.02 }}
                    type="text"
                    name="birthDate"
                    placeholder="Example: 1993-08-17"
                    value={personalData.birthDate}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-[#f784ff] focus:border-transparent outline-none transition-all"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    We'll automatically convert your birth date to the lunar
                    calendar for accurate calculation.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Birth Time
                  </label>
                  <motion.select
                    whileFocus={{ scale: 1.02 }}
                    name="birthTime"
                    value={personalData.birthTime}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-[#f784ff] focus:border-transparent outline-none transition-all appearance-none"
                  >
                    {birthTimeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </motion.select>
                  <p className="text-xs text-gray-500 mt-1">
                    Pick your birth hour. If you're unsure, select "I don't
                    know."
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City of Birth
                  </label>
                  <motion.input
                    whileFocus={{ scale: 1.02 }}
                    type="text"
                    name="birthPlace"
                    placeholder="Example: Dubai"
                    value={personalData.birthPlace}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-[#f784ff] focus:border-transparent outline-none transition-all"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Used to align your local time zone and cosmic patterns.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gender
                  </label>
                  <div className="flex space-x-4">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`flex-1 py-3 px-4 rounded-lg border ${
                        personalData.gender === "Male"
                          ? "bg-[#f784ff20] border-[#f784ff]"
                          : "bg-white"
                      } cursor-pointer transition-all`}
                      onClick={() =>
                        setPersonalData((prev) => ({ ...prev, gender: "Male" }))
                      }
                    >
                      <div className="flex flex-col items-center justify-center space-y-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="7" r="5" />
                          <path d="M12 12v9" />
                          <path d="M8 16h8" />
                        </svg>
                        <span className="text-xs text-center">Male</span>
                      </div>
                    </motion.div>

                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`flex-1 py-3 px-4 rounded-lg border ${
                        personalData.gender === "Female"
                          ? "bg-[#f784ff20] border-[#f784ff]"
                          : "bg-white"
                      } cursor-pointer transition-all`}
                      onClick={() =>
                        setPersonalData((prev) => ({
                          ...prev,
                          gender: "Female",
                        }))
                      }
                    >
                      <div className="flex flex-col items-center justify-center space-y-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="7" r="5" />
                          <path d="M12 12v9" />
                          <path d="M9 18h6" />
                          <path d="M12 21v-3" />
                        </svg>
                        <span className="text-xs text-center">Female</span>
                      </div>
                    </motion.div>

                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`flex-1 py-3 px-4 rounded-lg border ${
                        personalData.gender === "Prefer not to say"
                          ? "bg-[#f784ff20] border-[#f784ff]"
                          : "bg-white"
                      } cursor-pointer transition-all`}
                      onClick={() =>
                        setPersonalData((prev) => ({
                          ...prev,
                          gender: "Prefer not to say",
                        }))
                      }
                    >
                      <div className="flex flex-col items-center justify-center space-y-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 8v4" />
                          <path d="M12 16h.01" />
                        </svg>
                        <span className="text-xs text-center">Secret</span>
                      </div>
                    </motion.div>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Output Language
                  </label>
                  <div className="flex space-x-4">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`flex-1 py-3 px-4 rounded-lg border ${
                        personalData.language === "chinese"
                          ? "bg-[#f784ff20] border-[#f784ff]"
                          : "bg-white"
                      } cursor-pointer transition-all`}
                      onClick={() =>
                        setPersonalData((prev) => ({ ...prev, language: "chinese" }))
                      }
                    >
                      <div className="flex flex-col items-center justify-center space-y-1">
                        <span className="text-xs text-center">中文</span>
                      </div>
                    </motion.div>

                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`flex-1 py-3 px-4 rounded-lg border ${
                        personalData.language === "english"
                          ? "bg-[#f784ff20] border-[#f784ff]"
                          : "bg-white"
                      } cursor-pointer transition-all`}
                      onClick={() =>
                        setPersonalData((prev) => ({
                          ...prev,
                          language: "english",
                        }))
                      }
                    >
                      <div className="flex flex-col items-center justify-center space-y-1">
                        <span className="text-xs text-center">English</span>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>

              <motion.button
                type="submit"
                className={`mt-8 w-full py-3 rounded-lg font-medium transition-all shadow-md flex items-center justify-center ${
                  isLoading || !isFormValid()
                    ? "bg-gray-400 text-gray-200 cursor-not-allowed opacity-70"
                    : "bg-gradient-to-r from-[#f784ff] to-[#b784ff] text-white hover:opacity-90"
                }`}
                whileHover={{
                  scale: isLoading || !isFormValid() ? 1 : 1.03,
                  boxShadow: isLoading || !isFormValid() ? "none" : "0 10px 25px -5px rgba(247, 132, 255, 0.4)",
                }}
                whileTap={{ scale: isLoading || !isFormValid() ? 1 : 0.97 }}
                disabled={isLoading || !isFormValid()}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Analyzing...
                  </div>
                ) : (
                  "Start Analysis"
                )}
              </motion.button>
            </motion.form>
          )}

          {/* Result page */}
          {step === "result" && (
            <motion.div
              className="w-full bg-white/90 backdrop-blur-sm rounded-[16px] p-6 shadow-lg flex flex-col h-[calc(100vh-160px)] overflow-hidden"
              key="result"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
              style={{ display: "flex", flexDirection: "column" }}
            >
              {/* Loading animation */}
              {isLoading && !result && (
                <div className="flex-1 flex flex-col items-center justify-center">
                  <img src={spinningGif} alt="loading" className="w-8 h-8 mr-2" />
                  <span className="mr-2">{aiStatusText}</span>
                </div>
              )}

              {/* Result content */}
              {result && (
                <div
                  className="flex-1 flex flex-col"
                  style={{ height: "calc(100vh - 250px)" }}
                >
                  <div
                    ref={resultRef}
                    className="flex-1 overflow-y-auto pr-2 styled-scrollbar"
                    style={{
                      height: streamComplete ? "100%" : "calc(100% - 40px)",
                      scrollBehavior: "smooth",
                    }}
                  >
                    <div
                      id="horoscope-card"
                      className="bg-white p-4 rounded-lg shadow-sm"
                    >
                      <MarkdownRenderer content={result} />
                    </div>
                  </div>

                  {/* Output prompt */}
                  {!streamComplete && (
                    <div className="mt-4 text-center py-2 bg-white/80 backdrop-blur-sm">
                      <motion.div
                        className="inline-flex items-center text-[#f784ff]"
                        animate={{ opacity: [0.4, 1, 0.4] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        <img src={spinningGif} alt="loading" className="w-5 h-5 mr-2" />
                        <span className="mr-2">{aiStatusText}</span>
                        <span className="flex space-x-1">
                          <motion.span
                            animate={{ y: [0, -5, 0] }}
                            transition={{
                              duration: 0.6,
                              repeat: Infinity,
                              delay: 0,
                            }}
                          >
                            .
                          </motion.span>
                          <motion.span
                            animate={{ y: [0, -5, 0] }}
                            transition={{
                              duration: 0.6,
                              repeat: Infinity,
                              delay: 0.2,
                            }}
                          >
                            .
                          </motion.span>
                          <motion.span
                            animate={{ y: [0, -5, 0] }}
                            transition={{
                              duration: 0.6,
                              repeat: Infinity,
                              delay: 0.4,
                            }}
                          >
                            .
                          </motion.span>
                        </span>
                      </motion.div>
                    </div>
                  )}

                  {/* 分享按钮 */}
                  {streamComplete && (
                    <div className="flex flex-col items-center gap-3 mb-4">
                      <ShareButtons
                        cardId="horoscope-card"
                        data={result}
                        featureName="我的星座运势"
                        shareText={`See my fortune and then try yours: https://dapp.pinai.tech\n${result?.fortune || 'Fortune Telling'}`}
                      />
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* 悬浮提示词编辑组件 */}
        <PromptEditor
          updatePrompt={currentPrompt}
          onSendPrompt={handleSendPrompt}
        />
      </div>
    </div>
  );
};

export default HoroscopeDetail;
